import { IsStringEmpty, IsVarEmpty } from './NonReact/utils/GlobalFunctions'
import { PlaceInGlobalNameSpace } from './NonReact/utils/Utils'

// String whitespace trimming functions.
//
// Trim both sides.
//
export function Trim(sString: string): string | null {
    return IsStringEmpty(sString) ? null : sString.toString().replace(/^\s+|\s+$/g, '')
}

//
// Left side trim.
//
export function TrimL(sString: string): string | null {
    return IsStringEmpty(sString) ? null : sString.toString().replace(/^\s+/, '')
}

//
// Right side trim.
//
export function TrimR(sString: string): string | null {
    return IsStringEmpty(sString) ? null : sString.toString().replace(/\s+$/, '')
}

/**
 * Takes a string and returns a string equal to the given string with no adjacent spaces.
 *
 * Examples:
 * <ul>
 *      <li>TrimAllSpaces(" cat") = "cat"</li>
 *      <li>TrimAllSpaces("cat ") = "cat"</li>
 *      <li>TrimAllSpaces(" cat ") = "cat"</li>
 *      <li>TrimAllSpaces("cats    and      dogs") = "cats and dogs"</li>
 *      <li>TrimAllSpaces("   cats    and      dogs  ") = "cats and dogs"</li>
 * </ul>
 *
 * @param {String} sString String to trim
 *
 * @return {String} Null if the string given is empty, otherwise the same string with all multiple adjacent spaces removed.
 */
export function TrimFull(sString: string): string | null {
    const sTrimmed = Trim(sString.toString())
    return IsStringEmpty(sTrimmed) ? null : sTrimmed.replace(/\s+/g, ' ')
}

// Generates a text string from the specified byte array, encoding the byte number sequence as lower case readable hex values (e.g. [129, 0, 10, 32] => "81000a20").
//
export function HexStrFromBytes(pArrayOfBytes: number[]): string {
    let sHexStr = ''
    //
    let iByte
    const iByteCount = pArrayOfBytes === null ? 0 : pArrayOfBytes.length
    for (let i = 0; i < iByteCount; i += 1) {
        // Truncate each number to represent a byte value.
        // eslint-disable-next-line
        iByte = pArrayOfBytes[i] & 0xff
        // Encode as hex.
        // eslint-disable-next-line
        sHexStr += ((iByte >>> 4) & 0x0f).toString(16) // Most significant nibble.
        // eslint-disable-next-line
        sHexStr += (iByte & 0x0f).toString(16) // Least significant nibble.
    }
    //
    return sHexStr.toLowerCase()
}

// Converts a sequence of readable hex values to a byte array (e.g. "81000a20" => [129, 0, 10, 32]).
// If an odd number of characters are specified, then the last odd character is ignored completely.
// Characters that are not the valid hex construction characters a-f, A-F, & 0-9 are simply interpreted as 0 value nibbles, e.g. "as" would be converted to the byte value 160 (0xa0).
//
export function HexStrToBytes(sHexStr: string): number[] {
    const pArrayOfBytes = []
    //
    let sChar
    let iNibble
    let iByte = 0
    const iHexStrLen = IsStringEmpty(sHexStr) ? 0 : sHexStr.length
    for (let i = 0; i < iHexStrLen; i += 1) {
        // Get current character and convert to a 4 bit value.
        sChar = sHexStr.charAt(i)
        iNibble = parseInt(sChar, 16)
        // eslint-disable-next-line
        iNibble = IsVarEmpty(iNibble) || isNaN(iNibble) || !isFinite(iNibble) ? 0 : iNibble & 0x0f
        //
        if (i % 2 === 0) {
            // Odd character.
            // eslint-disable-next-line
            iByte = iNibble << 4
        } else {
            // Even character.
            iByte += iNibble
            pArrayOfBytes.push(iByte)
            //
            iByte = 0
        }
    }
    //
    return pArrayOfBytes
}

// Converts a sequence of readable hex values to a text string where each generated character is the Unicode encoding of the byte value represented by sequential pairs of hex digits within
// the originally provided String value.
// If an odd number of characters are specified, then the last odd character is ignored completely.
// Characters that are not the valid hex construction characters a-f, A-F, & 0-9 are simply interpreted as 0 value nibbles, e.g. "as" would be converted to the byte value 160 (0xa0).
//
export function HexStrToByteStr(sHexStr: string): string {
    let sByteStr = ''
    //
    let sChar
    let iNibble
    let iByte = 0
    const iHexStrLen = IsStringEmpty(sHexStr) ? 0 : sHexStr.length
    for (let i = 0; i < iHexStrLen; i += 1) {
        // Get current character and convert to 4 bit value.
        sChar = sHexStr.charAt(i)
        iNibble = parseInt(sChar, 16)
        // eslint-disable-next-line
        iNibble = IsVarEmpty(iNibble) || isNaN(iNibble) || !isFinite(iNibble) ? 0 : iNibble & 0x0f
        //
        if (i % 2 === 0) {
            // Odd character.
            // eslint-disable-next-line
            iByte = iNibble << 4
        } else {
            // Even character.
            iByte += iNibble
            sByteStr += String.fromCharCode(iByte)
            //
            iByte = 0
        }
    }
    //
    return sByteStr
}

// Generates a text string from the specified byte array, where each character is the Unicode encoding of each corresponding byte value in the original array.
//
export function ByteStrFromBytes(pArrayOfBytes: number[]): string {
    let sByteStr = ''
    //
    let iByte
    const iByteCount = pArrayOfBytes === null ? 0 : pArrayOfBytes.length
    for (let i = 0; i < iByteCount; i += 1) {
        // Truncate each number to represent a byte value.
        // eslint-disable-next-line
        iByte = pArrayOfBytes[i] & 0xff
        // Encode as corresponding Unicode character.
        sByteStr += String.fromCharCode(iByte)
    }
    //
    return sByteStr
}

// String start & end string match detection functions.
//
// Left side.
//
export function StartsWith(sString: string, sSearch: string): boolean {
    return IsStringEmpty(sString) || IsStringEmpty(sSearch) ? false : sString.startsWith(sSearch)
}

//
// Right side.
//
export function EndsWith(sString: string, sSearch: string): boolean {
    let bEndsWidth = false
    if (!IsStringEmpty(sString) && !IsStringEmpty(sSearch)) {
        const iStrLen = sString.length
        const iSrchLen = sSearch.length
        bEndsWidth = sString.lastIndexOf(sSearch) === iStrLen - iSrchLen
    }
    return bEndsWidth
}

export const Uncapitalize = (str: string): string => str.charAt(0).toLowerCase() + str.slice(1)

PlaceInGlobalNameSpace('ByteStrFromBytes', ByteStrFromBytes)
PlaceInGlobalNameSpace('HexStrToByteStr', HexStrToByteStr)
PlaceInGlobalNameSpace('HexStrToBytes', HexStrToBytes)
PlaceInGlobalNameSpace('HexStrFromBytes', HexStrFromBytes)
PlaceInGlobalNameSpace('Trim', Trim)
PlaceInGlobalNameSpace('TrimL', TrimL)
PlaceInGlobalNameSpace('TrimR', TrimR)
PlaceInGlobalNameSpace('TrimFull', TrimFull)
PlaceInGlobalNameSpace('StartsWith', StartsWith)
PlaceInGlobalNameSpace('EndsWith', EndsWith)
